import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Building2, Loader2, Plus, Edit3, Trash2, Crown, Upload, X, ImageIcon } from "lucide-react";
import {
  PageContainer,
  PageHeader,
  Card,
  Pill,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isMultiBranchFeatureEnabled } from "@/lib/permissions";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { toast } from "sonner";

const GCC_COUNTRIES = [
  { code: "AE", name: "United Arab Emirates", currency: "AED", decimals: 2 },
  { code: "SA", name: "Saudi Arabia", currency: "SAR", decimals: 2 },
  { code: "KW", name: "Kuwait", currency: "KWD", decimals: 2 },
  { code: "BH", name: "Bahrain", currency: "BHD", decimals: 2 },
  { code: "QA", name: "Qatar", currency: "QAR", decimals: 2 },
  { code: "OM", name: "Oman", currency: "OMR", decimals: 3 },
];

function Field({ label, children, span }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <Label className="text-[12px] text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

const emptyCompanyForm = {
  name: "",
  legal_name: "",
  trade_license: "",
  tax_number: "",
  country_code: "AE",
  currency: "AED",
  phone: "",
  email: "",
  website: "",
  address: "",
  logo_url: "",
  receipt_logo_enabled: false,
};

const emptyBranchForm = {
  name: "",
  code: "",
  brand_name: "",
  city: "",
  phone: "",
  address: "",
  is_active: true,
};

function apiError(err, fallback) {
  const errors = err.response?.data?.errors;
  if (errors) return Object.values(errors).flat()[0];
  return err.response?.data?.message || fallback;
}

export default function CompanySetup() {
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuth();
  const multiBranch = isMultiBranchFeatureEnabled(user);
  const [form, setForm] = useState(emptyCompanyForm);
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [branchForm, setBranchForm] = useState(emptyBranchForm);
  const logoInputRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const { data: companyData, isLoading } = useQuery({
    queryKey: ["company"],
    queryFn: async () => {
      const { data } = await api.get("/company");
      return data;
    },
  });

  const company = companyData?.company;
  const gccCountries = companyData?.gcc_countries?.length ? companyData.gcc_countries : GCC_COUNTRIES;
  const branches = company?.branches ?? [];

  useEffect(() => {
    if (!company) return;
    setForm({
      name: company.name || "",
      legal_name: company.legal_name || "",
      trade_license: company.trade_license || "",
      tax_number: company.tax_number || "",
      country_code: company.country_code || "AE",
      currency: company.currency || "AED",
      phone: company.phone || "",
      email: company.email || "",
      website: company.website || "",
      address: company.address || "",
      logo_url: company.logo_url || "",
      receipt_logo_enabled: Boolean(company.receipt_logo_enabled),
    });
  }, [company]);

  const saveCompanyMutation = useMutation({
    mutationFn: () =>
      api.put("/company", {
        name: form.name,
        legal_name: form.legal_name,
        trade_license: form.trade_license,
        tax_number: form.tax_number,
        country_code: form.country_code,
        phone: form.phone,
        email: form.email,
        website: form.website,
        address: form.address,
        logo_url: form.logo_url,
        receipt_logo_enabled: Boolean(form.receipt_logo_enabled),
      }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["company"] });
      await refreshUser?.();
      toast.success("Company saved");
    },
    onError: (err) => toast.error(apiError(err, "Failed to save company")),
  });

  const saveBranchMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: branchForm.name.trim(),
        code: branchForm.code.trim().toUpperCase(),
        brand_name: branchForm.brand_name?.trim() || null,
        city: branchForm.city?.trim() || null,
        phone: branchForm.phone?.trim() || null,
        address: branchForm.address?.trim() || null,
        is_active: branchForm.is_active,
      };
      if (editingBranchId) {
        return api.put(`/branches/${editingBranchId}`, payload);
      }
      return api.post("/branches", payload);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["company"] });
      setBranchDialogOpen(false);
      setEditingBranchId(null);
      setBranchForm(emptyBranchForm);
      toast.success(res.data.message || "Branch saved");
    },
    onError: (err) => toast.error(apiError(err, "Failed to save branch")),
  });

  const deleteBranchMutation = useMutation({
    mutationFn: (id) => api.delete(`/branches/${id}`),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["company"] });
      toast.success(res.data.message || "Branch deleted");
    },
    onError: (err) => toast.error(apiError(err, "Cannot delete branch — it may have sales or counters")),
  });

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setBranchField(key, value) {
    setBranchForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const body = new FormData();
      body.append("logo", file);
      const { data } = await api.post("/company/logo", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setField("logo_url", data.logo_url || "");
      queryClient.invalidateQueries({ queryKey: ["company"] });
      toast.success("Logo uploaded. Enable “Show logo on invoice” and Save Company.");
    } catch (err) {
      toast.error(apiError(err, "Logo upload failed"));
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  }

  function openNewBranch() {
    setEditingBranchId(null);
    setBranchForm(emptyBranchForm);
    setBranchDialogOpen(true);
  }

  function openEditBranch(branch) {
    setEditingBranchId(branch.id);
    setBranchForm({
      name: branch.name || "",
      code: branch.code || "",
      brand_name: branch.brand_name || "",
      city: branch.city || "",
      phone: branch.phone || "",
      address: branch.address || "",
      is_active: branch.is_active !== false,
    });
    setBranchDialogOpen(true);
  }

  function handleDeleteBranch(branch) {
    if (!window.confirm(`Delete branch "${branch.name}"? This only works if the branch has no linked data.`)) {
      return;
    }
    deleteBranchMutation.mutate(branch.id);
  }

  function handleSaveBranch() {
    if (!branchForm.name.trim()) {
      toast.error("Branch name is required");
      return;
    }
    if (!branchForm.code.trim()) {
      toast.error("Branch code is required");
      return;
    }
    saveBranchMutation.mutate();
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="h-40 grid place-items-center text-[13px] text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2 inline" />
          Loading company…
        </div>
      </PageContainer>
    );
  }

  const logoPreview = resolveMediaUrl(form.logo_url);
  const initials = form.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <PageContainer>
      <PageHeader
        title="Company Setup"
        subtitle="Legal entity details print on tax invoices (name, phone, address, TRN, optional logo)"
        actions={
          <Button
            size="sm"
            className="h-9 bg-[#16A34A] hover:bg-emerald-700"
            data-testid="company-save"
            onClick={() => saveCompanyMutation.mutate()}
            disabled={saveCompanyMutation.isPending}
          >
            {saveCompanyMutation.isPending ? (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            ) : (
              <Save size={14} className="mr-1.5" />
            )}
            Save Company
          </Button>
        }
      />
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="p-5 flex flex-col items-center text-center">
          <div className="h-24 w-24 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 grid place-items-center text-white text-[28px] font-black tracking-tight overflow-hidden">
            {logoPreview ? (
              <img src={logoPreview} alt="" className="h-full w-full object-contain bg-white" />
            ) : (
              initials || "—"
            )}
          </div>
          <div className="mt-3 text-[14px] font-semibold text-slate-900">{form.name}</div>
          <div className="text-[11.5px] text-slate-500">{branches.length} branch{branches.length !== 1 ? "es" : ""}</div>
          <div className="mt-4 w-full border-t pt-3 text-[11.5px] text-slate-500 space-y-1">
            <div>
              VAT (TRN): <span className="font-mono text-slate-700">{form.tax_number || "—"}</span>
            </div>
            <div>
              Phone: <span className="text-slate-700">{form.phone || "—"}</span>
            </div>
            <div>
              Email: <span className="text-slate-700">{form.email || "—"}</span>
            </div>
          </div>
        </Card>

        <Card className="p-5 xl:col-span-2">
          <SectionTitle>Company Information</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <Field label="Company Name *">
              <Input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                className="h-9"
                data-testid="company-name"
              />
            </Field>
            <Field label="Trade License Number">
              <Input
                value={form.trade_license}
                onChange={(e) => setField("trade_license", e.target.value)}
                className="h-9 font-mono"
              />
            </Field>
            <Field label="VAT / TRN Number">
              <Input
                value={form.tax_number}
                onChange={(e) => setField("tax_number", e.target.value)}
                className="h-9 font-mono"
              />
            </Field>
            <Field label="Country *">
              <Select
                value={form.country_code || "AE"}
                onValueChange={(code) => {
                  const row = gccCountries.find((c) => c.code === code) || GCC_COUNTRIES.find((c) => c.code === code);
                  setForm((prev) => ({
                    ...prev,
                    country_code: code,
                    currency: row?.currency || prev.currency,
                  }));
                }}
              >
                <SelectTrigger className="h-9" data-testid="company-country">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {gccCountries.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Currency">
              <Input
                value={`${form.currency || "AED"} · ${(gccCountries.find((c) => c.code === form.country_code) || GCC_COUNTRIES.find((c) => c.code === form.country_code))?.decimals ?? 2} decimals`}
                readOnly
                className="h-9"
                data-testid="company-currency"
              />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="h-9" />
            </Field>
            <Field label="Email">
              <Input value={form.email} onChange={(e) => setField("email", e.target.value)} className="h-9" />
            </Field>
            <Field label="Website">
              <Input value={form.website} onChange={(e) => setField("website", e.target.value)} className="h-9" />
            </Field>
            <Field label="Address" span={2}>
              <Textarea
                value={form.address}
                onChange={(e) => setField("address", e.target.value)}
                className="min-h-[64px]"
              />
            </Field>

            <div className="md:col-span-2 rounded-lg border border-[#E5E7EB] p-3 space-y-3">
              <div className="text-[12px] font-semibold text-slate-800">Invoice logo</div>
              <div className="flex gap-3 items-start">
                <div className="h-[72px] w-[72px] rounded-lg border bg-slate-50 overflow-hidden shrink-0 grid place-items-center">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo preview" className="h-full w-full object-contain" />
                  ) : (
                    <ImageIcon size={24} className="text-slate-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9"
                      disabled={logoUploading}
                      onClick={() => logoInputRef.current?.click()}
                      data-testid="company-logo-upload"
                    >
                      {logoUploading ? (
                        <Loader2 size={14} className="mr-1.5 animate-spin" />
                      ) : (
                        <Upload size={14} className="mr-1.5" />
                      )}
                      {logoUploading ? "Uploading…" : "Upload logo"}
                    </Button>
                    {form.logo_url ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-9 text-red-600"
                        onClick={() => setField("logo_url", "")}
                      >
                        <X size={14} className="mr-1" /> Remove
                      </Button>
                    ) : null}
                  </div>
                  <Input
                    value={form.logo_url}
                    onChange={(e) => setField("logo_url", e.target.value)}
                    className="h-9 font-mono text-[12px]"
                    placeholder="Or paste https://… /storage/… URL"
                  />
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={handleLogoUpload}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border px-3 py-2.5 bg-slate-50/60">
                <div>
                  <div className="text-[13px] font-medium text-slate-800">Show logo on invoice</div>
                  <div className="text-[11px] text-slate-500">
                    When on, prints the logo under TAX INVOICE (only if a logo is set)
                  </div>
                </div>
                <Switch
                  checked={Boolean(form.receipt_logo_enabled)}
                  onCheckedChange={(v) => setField("receipt_logo_enabled", v)}
                  data-testid="company-logo-on-invoice"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 border-t pt-4">
            <SectionTitle
              action={
                <div className="flex items-center gap-2">
                  <Pill tone="blue">{branches.length} {branches.length === 1 ? "outlet" : "branches"}</Pill>
                  {multiBranch ? (
                    <Button
                      size="sm"
                      className="h-8 bg-[#2563EB] hover:bg-blue-700"
                      data-testid="branch-new"
                      onClick={openNewBranch}
                    >
                      <Plus size={14} className="mr-1" /> New Branch
                    </Button>
                  ) : (
                    <Pill tone="amber">
                      <span className="inline-flex items-center gap-1">
                        <Crown size={11} /> Premium
                      </span>
                    </Pill>
                  )}
                </div>
              }
            >
              {multiBranch ? "Branches" : "Outlet"}
            </SectionTitle>
            {multiBranch ? (
              <p className="text-[11.5px] text-slate-500 mb-3">
                Add outlets here, then set up a counter under Administration → Counters and switch branch from the top bar.
              </p>
            ) : (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] leading-snug text-amber-900">
                <strong>Multi-branch</strong> is a Premium module. You can edit your current outlet
                details below. To add more branches and switch between them, unlock{" "}
                <em>Multi-branch &amp; branch switching</em> in the Central Console.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {branches.length === 0 ? (
                <div className="md:col-span-2 text-center py-8 text-slate-400 text-[13px] border border-dashed rounded-lg">
                  {multiBranch ? (
                    <>
                      No branches yet — click <strong>New Branch</strong>
                    </>
                  ) : (
                    <>No outlet configured yet.</>
                  )}
                </div>
              ) : (
                branches.map((b) => (
                  <div key={b.id} className="border border-[#E5E7EB] rounded-lg p-3 bg-slate-50/40">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-slate-800 text-[13px] flex items-center gap-1.5 min-w-0">
                        <Building2 size={14} className="text-blue-600 shrink-0" />
                        <span className="truncate">{b.name}</span>
                      </div>
                      <Pill tone={b.is_active ? "green" : "default"}>{b.is_active ? "Active" : "Inactive"}</Pill>
                    </div>
                    <div className="text-[11.5px] text-slate-500 mt-1 font-mono">{b.code}</div>
                    {b.phone ? (
                      <div className="text-[11.5px] text-slate-500 mt-0.5">{b.phone}</div>
                    ) : null}
                    {b.address ? (
                      <div className="text-[11px] text-slate-400 mt-1 line-clamp-2">{b.address}</div>
                    ) : null}
                    <div className="flex gap-2 mt-3 pt-2 border-t border-slate-200">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 flex-1"
                        data-testid={`branch-edit-${b.id}`}
                        onClick={() => openEditBranch(b)}
                      >
                        <Edit3 size={13} className="mr-1" /> Edit
                      </Button>
                      {multiBranch ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`branch-delete-${b.id}`}
                          onClick={() => handleDeleteBranch(b)}
                          disabled={deleteBranchMutation.isPending || branches.length <= 1}
                        >
                          <Trash2 size={13} className="mr-1" /> Delete
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </Card>
      </div>

      <Dialog open={branchDialogOpen} onOpenChange={setBranchDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingBranchId ? "Edit Branch" : "New Branch"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <Field label="Branch Name *">
              <Input
                value={branchForm.name}
                onChange={(e) => setBranchField("name", e.target.value)}
                className="h-9"
                placeholder="e.g. Marina Branch"
                data-testid="branch-name"
              />
            </Field>
            <Field label="Branch Code *">
              <Input
                value={branchForm.code}
                onChange={(e) => setBranchField("code", e.target.value.toUpperCase())}
                className="h-9 font-mono"
                placeholder="e.g. MARINA"
                maxLength={20}
                data-testid="branch-code"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Brand">
                <Input value={branchForm.brand_name} onChange={(e) => setBranchField("brand_name", e.target.value)} className="h-9" placeholder="e.g. Shawarma House" />
              </Field>
              <Field label="City">
                <Input value={branchForm.city} onChange={(e) => setBranchField("city", e.target.value)} className="h-9" placeholder="e.g. Dubai" />
              </Field>
            </div>
            <Field label="Phone">
              <Input
                value={branchForm.phone}
                onChange={(e) => setBranchField("phone", e.target.value)}
                className="h-9"
                data-testid="branch-phone"
              />
            </Field>
            <Field label="Address">
              <Textarea
                value={branchForm.address}
                onChange={(e) => setBranchField("address", e.target.value)}
                className="min-h-[72px]"
                data-testid="branch-address"
              />
            </Field>
            <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
              <div>
                <div className="text-[13px] font-medium text-slate-800">Active</div>
                <div className="text-[11px] text-slate-500">Inactive branches are hidden from day-to-day use</div>
              </div>
              <Switch
                checked={branchForm.is_active}
                onCheckedChange={(v) => setBranchField("is_active", v)}
                data-testid="branch-active"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBranchDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-[#16A34A] hover:bg-emerald-700"
              onClick={handleSaveBranch}
              disabled={saveBranchMutation.isPending}
              data-testid="branch-save"
            >
              {saveBranchMutation.isPending ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <Save size={14} className="mr-1.5" />
              )}
              {editingBranchId ? "Save Changes" : "Create Branch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
